import { DocumentTextRegular } from "@fluentui/react-icons";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo } from "react";
import { Accept, useDropzone } from "react-dropzone";

import styles from "./index.module.css";

import { generateId } from "@/utils/generateId";

export type DragAndDropAcceptFileType =
  | "excel"
  | "csv"
  | "json"
  | "yaml"
  | "zip";

export const dragAndDropAcceptFiles: {
  [acceptFileType in DragAndDropAcceptFileType]: Accept;
} = {
  excel: {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel": [".xls"],
  },
  csv: {
    "text/csv": [".csv"],
  },
  json: {
    "application/json": [".json"],
  },
  yaml: { "application/yaml": [".yml", ".yaml"] },
  zip: { "application/zip": [".zip"] },
};

const fileTypeNames = {
  excel: "Excelデータ",
  csv: "CSVデータ",
  json: "JSONデータ",
  yaml: "YAMLデータ",
  zip: "ZIPデータ",
};

export type DragAndDropUploadData = {
  file: File;
  id: string;
  isDisableDelete: boolean;
};

export type FileTypeConfig = {
  type: DragAndDropAcceptFileType;
  limit: number;
};

const getFileType = (file: File): DragAndDropAcceptFileType | undefined => {
  return (
    Object.keys(dragAndDropAcceptFiles) as DragAndDropAcceptFileType[]
  ).find((type) => {
    const exts = Object.values(dragAndDropAcceptFiles[type]).flat();
    return exts.some((ext) => file.name.toLowerCase().endsWith(ext));
  });
};

/**
 * @description ドラッグ&ドロップでデータをアップする部分を描画
 * アップロードされたデータのstateは外部から注入する前提
 */
export const DragAndDropFields: React.FC<{
  accept: Accept;
  uploadedFileList: DragAndDropUploadData[];
  setUploadedFileList: React.Dispatch<
    React.SetStateAction<DragAndDropUploadData[]>
  >;
  existingType: DragAndDropAcceptFileType | undefined;
  setExistingType: React.Dispatch<
    React.SetStateAction<DragAndDropAcceptFileType | undefined>
  >;
  fileTypeConfigs: FileTypeConfig[];
  extElement: React.ReactNode;
}> = ({
  accept,
  uploadedFileList,
  setUploadedFileList,
  existingType,
  setExistingType,
  fileTypeConfigs,
  extElement,
}) => {
  useEffect(() => {
    if (uploadedFileList.length === 0) {
      setExistingType(undefined);
      return;
    }
    setExistingType(getFileType(uploadedFileList[0].file));
  }, [uploadedFileList, setExistingType]);

  const currentLimit = useMemo(() => {
    if (!existingType) return 1;
    const cfg = fileTypeConfigs.find((c) => c.type === existingType);
    return cfg ? cfg.limit : 1;
  }, [existingType, fileTypeConfigs]);

  // 既存のアップロードデータがある場合は、そのデータのタイプに合わせてacceptを変更
  const dynamicAccept: Accept = useMemo(() => {
    if (!existingType) {
      return accept;
    }
    return dragAndDropAcceptFiles[existingType] ?? {};
  }, [existingType, accept]);

  const disableDragAndDrop = uploadedFileList.length >= currentLimit;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      let nowFilesNum = uploadedFileList.length;
      const newUploadedDataList: DragAndDropUploadData[] = [];
      // 限界までデータを追加
      for (const file of acceptedFiles) {
        if (nowFilesNum >= currentLimit) break;
        newUploadedDataList.push({
          file,
          id: generateId(),
          isDisableDelete: false,
        });
        nowFilesNum++;
      }
      // 状態更新
      setUploadedFileList((state) => [...state, ...newUploadedDataList]);
    },
    [setUploadedFileList, uploadedFileList, currentLimit]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dynamicAccept,
    disabled: disableDragAndDrop,
  });

  return (
    <div className={styles.dragAndDrop}>
      {
        <div
          className={classNames(styles.file, {
            [styles.drag]: isDragActive,
            [styles.disable]: disableDragAndDrop,
          })}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <DocumentTextRegular
            style={{ width: 24, height: 24, marginBottom: "16px" }}
          />
          ファイルをここにドラッグ&ドロップ
          <div>
            (
            {fileTypeConfigs.map((fileTypeConfig, index) => {
              const typeName =
                fileTypeNames[fileTypeConfig.type] ||
                `${fileTypeConfig.type}データ`;

              const separator = index < fileTypeConfigs.length - 1 ? "、" : "";

              return (
                <span key={fileTypeConfig.type}>
                  {typeName}
                  {fileTypeConfigs.length > 1 ? "は" : "を"}
                  {fileTypeConfig.limit}件まで{separator}
                </span>
              );
            })}
            アップロード可能)
          </div>
        </div>
      }
      {extElement}
    </div>
  );
};
